// ReminderPreview.jsx — review the next reminder, against everything already sent.
//
// A reminder series is one conversation as far as the client is concerned. So
// deciding what the next one should say means seeing what the last three
// actually said — which is why the sent history runs down the side like a
// contact timeline rather than being somewhere else entirely.
//
// WHY THE NEXT ONE IS WRITTEN EARLY. The body is normally composed at send
// time, inside the worker, which makes it unreviewable by definition: nobody
// sees it before the client does. "Write it now" runs the SAME drafter ahead of
// the send and parks the result, so what is reviewed here is exactly what goes
// out — including any edit made to it.
//
// Everything the recipient will receive is on this panel and editable: subject,
// To, CC, body, the attachment, and the date it fires.

import React, { useCallback, useEffect, useState } from "react";
import {
  getReminderTimeline, predraftReminder, editReminderDraft,
  fetchReminderAttachment, fetchSentReminderAttachment,
} from "../../api/invoices/invoices";
import DocumentViewer from "./DocumentViewer";
import { toLocalInputValue, startIsPending, fmtInZone } from "./scheduleTime";

const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : ''; } catch (_) { return String(d || ''); } };
const fmtDay = (d) => { try { return d ? new Date(d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : ''; } catch (_) { return ''; } };

/**
 * What became of a message that was written.
 *
 * The failure worth surfacing: a reminder drafted, counted, the cadence moved
 * a week on — and the mail still sitting in the send queue. Everything else on
 * the panel would look like a successful send.
 */
export function deliveryState(message) {
  if (!message) return null;
  const s = String(message.status || '').toLowerCase();
  if (s === 'sent') return { kind: 'sent', tone: '#1b7a44', bg: '#e8f5ee', text: `Sent ${fmt(message.sentAt) || ''}`.trim() };
  if (s === 'draft' || s === 'queued') return { kind: 'unsent', tone: '#8a5a00', bg: '#fff6e5', text: 'NOT SENT — written, but still waiting in the send queue' };
  if (s === 'failed' || s === 'bounced') {
    return { kind: 'failed', tone: '#a12626', bg: '#fdecec', text: `${s === 'bounced' ? 'Bounced' : 'Send failed'}${message.lastError ? `: ${message.lastError}` : ''}` };
  }
  return { kind: 'other', tone: '#5b6670', bg: '#f1f2f4', text: s || 'unknown' };
}

const parseList = (text) => String(text || '').split(/[\s,;]+/).map((s) => s.trim()).filter((s) => s.includes('@'));

const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * An attachment, as a link you can actually open.
 *
 * Until now this panel named the file and stopped there, which left the one
 * thing a reminder is judged on — is the right invoice attached? — the only
 * thing on it that could not be checked. Clicking opens the real bytes in the
 * shared viewer, over this panel rather than instead of it, so the email being
 * reviewed is still there behind the document.
 *
 * `available: false` is drawn but not clickable. An attachment row with no
 * bytes behind it is the exact failure this package shipped once — a reminder
 * that looked normal and carried nothing — so it is shown, greyed and labelled,
 * rather than quietly left out.
 */
export function AttachmentLink({ file, onOpen }) {
  const size = fmtBytes(file.sizeBytes || file.byteSize);
  if (file.available === false) {
    return (
      <span style={{ color: '#a12626' }} title="This attachment has no stored file behind it — the email went out without it">
        📎 {file.filename} (no file stored)
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`Open ${file.filename}`}
      style={{
        background: 'none', border: 'none', padding: 0, font: 'inherit',
        color: '#15559c', textDecoration: 'underline', cursor: 'pointer',
      }}
    >
      📎 {file.filename}{size ? ` (${size})` : ''}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#6b7a86', marginBottom: 3 }}>{label}</div>
      {children}
    </label>
  );
}

// ─────────────────────────────────────────────────────── the timeline column
function Timeline({ sent, next, onPick, picked }) {
  return (
    <div style={{ width: 250, flexShrink: 0, borderLeft: '1px solid #eef0f3', paddingLeft: 14, maxHeight: '62vh', overflowY: 'auto' }}>
      <div style={{ fontSize: 11, color: '#6b7a86', fontWeight: 600, marginBottom: 8 }}>THIS SERIES</div>

      {sent.length === 0 && (
        <div style={{ fontSize: 12, color: '#9aa4ad', marginBottom: 10 }}>Nothing sent yet.</div>
      )}

      {sent.map((m, i) => {
        const d = deliveryState(m);
        const on = picked === m._id;
        return (
          <div
            key={m._id}
            onClick={() => onPick(on ? null : m._id)}
            style={{
              borderLeft: `3px solid ${d.tone}`, paddingLeft: 8, marginBottom: 10, cursor: 'pointer',
              background: on ? '#f4f7fb' : 'transparent', borderRadius: on ? 4 : 0, paddingTop: 2, paddingBottom: 4,
            }}
          >
            <div style={{ fontSize: 11, color: d.tone, fontWeight: 600 }}>
              #{i + 1} · {m.sentAt ? fmtDay(m.sentAt) : 'not sent'}
            </div>
            <div style={{ fontSize: 12, color: '#33414d', fontWeight: 500 }}>{m.subject || '(no subject)'}</div>
            <div style={{ fontSize: 11, color: '#9aa4ad', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {(m.bodyText || '').replace(/\s+/g, ' ').slice(0, 60)}
            </div>
          </div>
        );
      })}

      {/* The one that has not gone yet, always last and always visible — the
          point of the panel is deciding about THIS one. */}
      <div style={{
        borderLeft: '3px dashed #15172b', paddingLeft: 8, marginTop: 4, paddingTop: 2, paddingBottom: 4,
        background: picked === null ? '#f4f7fb' : 'transparent', borderRadius: picked === null ? 4 : 0, cursor: 'pointer',
      }} onClick={() => onPick(null)}>
        <div style={{ fontSize: 11, color: '#15172b', fontWeight: 600 }}>
          NEXT · {next.scheduledFor ? fmtDay(next.scheduledFor) : 'not scheduled'}
        </div>
        <div style={{ fontSize: 12, color: '#33414d', fontWeight: 500 }}>
          {next.preDrafted ? (next.subject || '(no subject)') : 'not written yet'}
        </div>
        <div style={{ fontSize: 11, color: '#9aa4ad' }}>
          {next.willSend ? (next.scheduledFor ? fmt(next.scheduledFor) : '') : 'sending is off'}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────── the panel
export default function ReminderPreview({ reminder, invoices, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState(null);     // a sent message id, or null = the next one
  // The attachment being read, as { title, note, load }. Held here rather than
  // inside the link so the viewer stacks OVER this panel — the email under
  // review stays on screen behind the document it carries.
  const [openDoc, setOpenDoc] = useState(null);

  // The editable next reminder.
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [when, setWhen] = useState('');
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await getReminderTimeline(reminder._id);
      const d = (r && r.data) || null;
      setData(d);
      if (d) {
        setSubject(d.next.subject || '');
        setBodyText(d.next.bodyText || '');
        setTo((d.next.to || []).join(', '));
        setCc((d.next.cc || []).join(', '));
        // Local, not UTC. toISOString().slice(0,16) feeds a UTC instant into a
        // box that reads it as local time, so an operator west of UTC opened
        // this panel and found the send hour had moved.
        setWhen(toLocalInputValue(d.next.scheduledFor));
        setDirty(false);
      }
    } catch (e) { setErr((e && e.message) || 'Could not load this series'); }
    finally { setLoading(false); }
  }, [reminder._id]);

  useEffect(() => { load(); }, [load]);

  const write = async () => {
    setBusy(true); setErr(''); setNote('');
    try {
      await predraftReminder(reminder._id);
      setNote('Writing it now — this uses the same drafter that would run at send time. Refresh in a few seconds.');
    } catch (e) { setErr((e && e.message) || 'Could not start the draft'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr(''); setNote('');
    try {
      await editReminderDraft(reminder._id, {
        subject, bodyText,
        toAddresses: parseList(to),
        ccAddresses: parseList(cc),
        ...(when ? { nextReminderAt: new Date(when).toISOString() } : {}),
      });
      setNote('Saved. This is exactly what will be sent.');
      setDirty(false);
      await load();
      onChanged && onChanged();
    } catch (e) { setErr((e && e.message) || 'Could not save'); }
    finally { setBusy(false); }
  };

  const inv = data?.invoice
    || (invoices || []).find((i) => String(i._id) === String(reminder.invoice_id))
    || null;
  const sent = data?.sent || [];
  const next = data?.next || { to: [], cc: [] };
  const shown = picked ? sent.find((m) => m._id === picked) : null;
  const shownState = shown ? deliveryState(shown) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 960, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h5 className="mb-0">
            {inv ? `${inv.invoiceNumber || '(no number)'} — ${inv.currency || 'USD'} ${inv.total}` : 'Reminder series'}
          </h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>

        {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
        {note && <div className="alert alert-info" style={{ fontSize: 12, padding: '6px 10px' }}>{note}</div>}
        {loading && <div className="cm-empty">Loading the series…</div>}

        {data && (
          <div style={{ display: 'flex', gap: 16 }}>
            {/* ── the email itself ── */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {shown ? (
                <>
                  <div style={{ background: shownState.bg, color: shownState.tone, borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
                    {shownState.text}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7a86', marginBottom: 6 }}>
                    {shown.from || '?'} → {(shown.to || []).join(', ')}
                    {(shown.cc || []).length > 0 && <> · cc {(shown.cc || []).join(', ')}</>}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{shown.subject || '(no subject)'}</div>
                  <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', color: '#33414d', background: '#fafbfc', borderRadius: 6, padding: 12 }}>
                    {shown.bodyText || '(no body stored)'}
                  </div>
                  {(shown.attachments || []).length > 0 && (
                    <div style={{ fontSize: 11, color: '#6b7a86', marginTop: 8, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {/* What the client actually received, openable. This is
                          the only way to answer "did the right invoice go
                          out?" about an email that has already gone. */}
                      {shown.attachments.map((a) => (
                        <AttachmentLink
                          key={`${shown._id}-${a.index}`}
                          file={a}
                          onOpen={() => setOpenDoc({
                            title: a.filename,
                            note: `sent ${fmt(shown.sentAt) || 'with this reminder'}`,
                            load: () => fetchSentReminderAttachment(reminder._id, shown._id, a.index),
                          })}
                        />
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#9aa4ad', marginTop: 10 }}>
                    An email already sent cannot be edited. Pick <b>NEXT</b> in the timeline to change the one still to come.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, borderRadius: 12, padding: '2px 10px',
                      background: next.preDrafted ? '#e8f5ee' : '#fff6e5',
                      color: next.preDrafted ? '#1b7a44' : '#8a5a00',
                    }}>
                      {next.preDrafted ? (next.edited ? 'Written and edited by you' : 'Written, ready to review') : 'Not written yet'}
                    </span>
                    {!next.preDrafted && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={write} disabled={busy}>
                        Write it now
                      </button>
                    )}
                    {next.preDrafted && (
                      <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={write} disabled={busy}>
                        Rewrite from scratch
                      </button>
                    )}
                    <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={load} disabled={busy || loading}>Refresh</button>
                  </div>

                  {!next.preDrafted && (
                    <div style={{ fontSize: 12, color: '#6b7a86', marginBottom: 10 }}>
                      The body is normally written the moment it sends, so nobody sees it before the client
                      does. Writing it now runs the same drafter early and parks the result here, where you
                      can change any of it. Whatever is here is exactly what goes out.
                    </div>
                  )}

                  <Field label="Subject">
                    <input className="form-control" value={subject} placeholder="written when you press Write it now"
                      onChange={(e) => { setSubject(e.target.value); setDirty(true); }} />
                  </Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="To">
                      <input className="form-control" value={to} onChange={(e) => { setTo(e.target.value); setDirty(true); }} />
                    </Field>
                    <Field label="CC — inherited from the client record">
                      <input className="form-control" value={cc} placeholder="nobody" onChange={(e) => { setCc(e.target.value); setDirty(true); }} />
                    </Field>
                  </div>
                  <Field label="Body">
                    <textarea className="form-control" rows={10} value={bodyText}
                      placeholder="written when you press Write it now"
                      onChange={(e) => { setBodyText(e.target.value); setDirty(true); }} />
                  </Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Sends on">
                      <input type="datetime-local" className="form-control" value={when}
                        onChange={(e) => { setWhen(e.target.value); setDirty(true); }} />
                      {/* A flow that is switched on but holding for a chosen
                          start is the state most easily mistaken for a broken
                          one: sending says ON and no email has gone. Say why,
                          here, where the operator came to find out. */}
                      {next.willSend && startIsPending(next.scheduledFor) && (
                        <span style={{ fontSize: 11, color: '#8a5a00' }}>
                          Armed — nothing has been emailed yet.
                        </span>
                      )}
                      {!next.willSend && startIsPending(next.scheduledStartAt) && (
                        <span style={{ fontSize: 11, color: '#8a5a00' }}>
                          Set to begin {fmtInZone(next.scheduledStartAt, next.scheduleTimeZone)} once Sending is switched on.
                        </span>
                      )}
                    </Field>
                    <Field label="Attachment">
                      {/* Named AND openable. "the invoice's own document" was
                          true and useless: it did not say which file that
                          resolved to, and there was no way to look at it. The
                          server resolves the same chain the drafter uses, so
                          what opens here is what the client receives. */}
                      <div style={{ fontSize: 13, paddingTop: 6 }}>
                        {next.attachmentFile ? (
                          <>
                            <AttachmentLink
                              file={next.attachmentFile}
                              onOpen={() => setOpenDoc({
                                title: next.attachmentFile.filename,
                                note: next.attachmentFile.sourceLabel,
                                load: () => fetchReminderAttachment(reminder._id),
                              })}
                            />
                            {next.attachmentFile.sourceLabel && (
                              <div style={{ fontSize: 11, color: '#8a949c' }}>{next.attachmentFile.sourceLabel}</div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#a12626' }} title="No PDF on the reminder and no document on the invoice">
                            nothing to attach — this invoice has no document
                          </span>
                        )}
                      </div>
                    </Field>
                  </div>

                  <div style={{ fontSize: 11, color: '#9aa4ad' }}>
                    The brand signature is appended at send time and is not shown here.
                    {!next.willSend && <b> Sending is off for this flow, so nothing is scheduled.</b>}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                    <button className="acc-btn acc-btn-primary" onClick={save} disabled={busy || !dirty}>
                      {busy ? 'Saving…' : 'Save this email'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <Timeline sent={sent} next={next} onPick={setPicked} picked={picked} />
          </div>
        )}

        {/* Above this panel (z-index 70 over its 50), so closing the document
            returns to the email rather than to the list. */}
        {openDoc && (
          <DocumentViewer
            key={openDoc.title}
            title={openDoc.title}
            note={openDoc.note}
            load={openDoc.load}
            zIndex={70}
            onClose={() => setOpenDoc(null)}
          />
        )}
      </div>
    </div>
  );
}
